import React, { createContext, useContext, useEffect, useState } from 'react'
import { api, ApiRoutes } from '../services/ApiService'
import Loader from '../components/Loader'
import cx from 'classnames'
import styles from '../App.module.css'
import BlankNavigation from '../components/Navigation/BlankNavigation'
import { showToast } from '../services/ToastService'
import { useTranslation } from 'react-i18next'

type AuthContextType = {
    login: (email: string, password: string) => void
    check: () => void
    logout: () => void
    role: 'user' | 'fulfiller' | 'admin'
    loggedIn: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

type AuthContextProps = {
    children: React.ReactNode | React.ReactNode[]
}

export const AuthContextProvider = ({
    children,
}: AuthContextProps): JSX.Element => {
    const { t } = useTranslation(['login'])
    const [loggedIn, setLoggedIn] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [role, setRole] = useState<'user' | 'fulfiller' | 'admin'>('user')

    const login = async (email: string, password: string) => {
        api.post(ApiRoutes.LOGIN, undefined, {
            email: email,
            password: password,
        })
            .then(({ data }) => {
                if (data.data) {
                    localStorage.setItem('zr_customizer_token', data.data.token)
                    setRole(data.data.role)
                }
                setLoggedIn(true)
                showToast(t('login.success'), 'success')
            })
            .catch((e) => {
                setLoggedIn(false)
                showToast(t('login.error'), 'error')
            })
    }

    const logout = () => {
        api.get(ApiRoutes.LOGOUT).then(() => {
            setLoggedIn(false)
            setRole('user')
            localStorage.removeItem('zr_customizer_token')
            showToast(t('logout.success'), 'success')
        })
    }

    const check = () => {
        api.get(ApiRoutes.CHECK_AUTH)
            .then(({ data }) => {
                if (data.data) {
                    localStorage.setItem('zr_customizer_token', data.data.token)
                    setRole(data.data.role)
                }
                setLoggedIn(true)
            })
            .catch(() => {
                setLoggedIn(false)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        check()
    }, [])

    return (
        <AuthContext.Provider
            value={{
                loggedIn,
                login,
                check,
                logout,
                role,
            }}
        >
            {loading ? (
                <div className={cx(styles['zr-main-container'])}>
                    <BlankNavigation />
                    <div className={cx(styles['zr-main-content'])}>
                        <div
                            className={
                                'zr-w-100 zr-h-100 zr-flex zr-justify-center zr-items-center'
                            }
                        >
                            <Loader isLoading={loading} size={'s'} />
                        </div>
                    </div>
                </div>
            ) : (
                children
            )}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const data = useContext(AuthContext)

    if (data === undefined) {
        throw new Error('useAuth must used within AuthContextProvider')
    }

    return data
}
